/**
 * Canonical null-safe money/number/time formatting.
 *
 * Every API field can be null/undefined, and an unguarded `value.toFixed(2)`
 * throws "Cannot read properties of null", crashing the whole React render
 * (blank page). Route display formatting through these helpers so a null/NaN
 * value degrades to a dash instead of taking the page down.
 */

/** The one missing-data glyph. */
export const DASH = '—'

/** Unicode minus — reads better than the ASCII hyphen next to £. */
const MINUS = '−'

function _bad(n: number | null | undefined): boolean {
  return n == null || typeof n !== 'number' || !Number.isFinite(n)
}

// ── Numbers & percentages ───────────────────────────────────────────────────

/** Fixed-decimal number, or '—' for null/undefined/NaN/Infinity.
 *  THE replacement for raw `.toFixed()` on API-derived values. */
export function fmtNum(n: number | null | undefined, dp = 2): string {
  return _bad(n) ? DASH : (n as number).toFixed(dp)
}

/** Signed fixed-decimal (always shows + on non-negatives), or '—'. */
export function fmtSigned(n: number | null | undefined, dp = 2): string {
  if (_bad(n)) return DASH
  const v = n as number
  return `${v >= 0 ? '+' : ''}${v.toFixed(dp)}`
}

/** Percentage with a trailing %, or '—'. */
export function fmtPct(n: number | null | undefined, dp = 1): string {
  return _bad(n) ? DASH : `${(n as number).toFixed(dp)}%`
}

/** Signed percentage (e.g. "+1.2%"), or '—'. */
export function fmtPctSigned(n: number | null | undefined, dp = 1): string {
  if (_bad(n)) return DASH
  const v = n as number
  return `${v >= 0 ? '+' : ''}${v.toFixed(dp)}%`
}

/** @deprecated alias for fmtPctSigned — kept for existing imports. */
export const fmtSignedPct = fmtPctSigned

// ── Money ───────────────────────────────────────────────────────────────────

/** GBP with thousands separators, sign BEFORE the symbol (−£1,234), or '—'. */
export function fmtGBP(n: number | null | undefined, dp = 0): string {
  if (_bad(n)) return DASH
  const v = n as number
  const body = Math.abs(v).toLocaleString('en-GB', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })
  return `${v < 0 ? MINUS : ''}£${body}`
}

/** Compact GBP: ≥£1M → £X.XXM, ≥£10k → £Xk, else full fmtGBP. '—' on null.
 *  Negatives keep the sign before the symbol (−£1.20M, not £-1.2M). */
export function fmtGBPCompact(n: number | null | undefined): string {
  if (_bad(n)) return DASH
  const v = n as number
  const abs = Math.abs(v)
  const sign = v < 0 ? MINUS : ''
  if (abs >= 1_000_000) return `${sign}£${(abs / 1_000_000).toFixed(2)}M`
  if (abs >= 10_000) return `${sign}£${(abs / 1_000).toFixed(0)}k`
  return fmtGBP(v)
}

/** GBP with an explicit +/− prefix (e.g. "+£1,234" / "−£1,234"), or '—'. */
export function fmtGBPSigned(n: number | null | undefined, dp = 0): string {
  if (_bad(n)) return DASH
  const v = n as number
  return `${v > 0 ? '+' : ''}${fmtGBP(v, dp)}`
}

/** Native-currency symbol for per-share price/cost columns — a $395.60 stock
 *  must not read as £395.60 (AJ Bell XO, 2026-07-16). Unknown codes → null
 *  so callers can fall back to a code prefix. */
export function ccySymbol(ccy: string | null | undefined): string | null {
  switch ((ccy ?? 'GBP').toUpperCase()) {
    case 'GBP': return '£'
    case 'USD': return '$'
    case 'EUR': return '€'
    case 'JPY': return '¥'
    default: return null
  }
}

/** Money in a native currency: known symbols (£ $ € ¥) prefixed, unknown
 *  codes rendered as "CHF 1,234.56". Sign before symbol. '—' on null. */
export function fmtMoney(
  n: number | null | undefined,
  ccy: string | null | undefined,
  dp = 2,
): string {
  if (_bad(n)) return DASH
  const v = n as number
  const body = Math.abs(v).toLocaleString('en-GB', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })
  const sign = v < 0 ? MINUS : ''
  const sym = ccySymbol(ccy)
  return sym != null
    ? `${sign}${sym}${body}`
    : `${sign}${(ccy ?? '').toUpperCase()} ${body}`
}

// ── Time ────────────────────────────────────────────────────────────────────

/** Human "time ago" ladder: "just now" / "3m ago" / "2h ago" / "yesterday" /
 *  "4d ago". '—' on null/unparseable. */
export function fmtAgo(iso: string | null | undefined): string {
  if (!iso) return DASH
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return DASH
  const sec = Math.floor(Math.max(0, Date.now() - t) / 1000)
  if (sec < 60) return 'just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  if (day < 2) return 'yesterday'
  return `${day}d ago`
}

/** en-GB short date-time, e.g. "16 Jul, 14:30". '—' on null/unparseable. */
export function fmtDateTime(iso: string | number | Date | null | undefined): string {
  if (iso == null || iso === '') return DASH
  const d = iso instanceof Date ? iso : new Date(iso)
  if (isNaN(d.getTime())) return DASH
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

/**
 * Date-fns `format()` THROWS RangeError on a null/garbage timestamp, and the
 * app has a single page-level ErrorBoundary — one bad API date crashed whole
 * pages. safeFormat degrades to the dash instead.
 */
export function safeFormat(
  value: string | number | Date | null | undefined,
  fmt: string,
  formatFn: (d: Date, f: string) => string,
): string {
  if (value === null || value === undefined || value === '') return DASH
  try {
    const d = value instanceof Date ? value : new Date(value)
    if (isNaN(d.getTime())) return DASH
    return formatFn(d, fmt)
  } catch {
    return DASH
  }
}
